/**
 * useDomainBundles — groups folder emails by primaryDomain and clusters
 * emails within each domain into topic groups.
 *
 * Primary grouping key: `subcategory` (LLM-assigned L3 classification).
 * Fallback (when subcategory is null): threadId, then normalised subject.
 */
import { useMemo } from 'react';

/** Domain display labels (UI-only, not data) */
const DOMAIN_LABELS = {
  staffing: 'Staffing',
  quality: 'Quality',
  access: 'Access',
  finance: 'Finance',
  consumer: 'Consumer',
  health: 'Pop Health',
};

/** AI-generated domain-level summaries (UI-only, not data) */
const DOMAIN_SUMMARIES = {
  staffing: 'Weekend coverage gap: 2 RN positions unfilled for Sat-Sun. Float pool request pending HR confirmation (deadline: Friday 5pm). Dr. Martinez credentialing renewal due June 1 — documentation package due May 15. Weekly huddle flagged staffing as top priority.',
  quality: 'Q1 A1C control rate dropped to 68% (target 75%), requiring outreach to 47 patients. HIPAA training deadline April 30 with 12 staff not yet started. Flu vaccine supply backordered 2-3 weeks — alternative available. EHR maintenance scheduled Saturday 2-4am. New immunization reporting rules effective July 1.',
  finance: 'Claim #4821-RX denied for missing prior auth ($285). March P&L shows revenue up 3% but denial rate climbed to 6.2% — director wants reduction plan by Friday. Draft denial reduction plan in progress.',
  access: 'URGENT: Wait times hit 42 minutes (target 15 min). Overflow room opened, trending back toward target. 8 no-shows today (9.4% rate, $1,350 in lost new-patient revenue). 28 referrals backlogged 14+ days — cardiology and endocrinology worst.',
  consumer: '3 patient complaints about phone hold times exceeding 10 minutes during 8-9am peak. 2-star Google review posted. Current staffing: 2 agents handling 55 calls/hr (capacity: 30). Suggested: add third agent or enable callback queue.',
  health: '24 high-risk patients not seen in 90+ days: 8 diabetic (overdue A1C), 6 uncontrolled hypertension, 5 post-surgical, 5 behavioral health referrals pending. Outreach calls scheduled this week.',
};

/** Strip Re:/Fwd:/FW: prefixes and lowercase for grouping */
function normaliseSubject(s) {
  return (s || '')
    .replace(/^(re|fwd|fw)\s*:\s*/gi, '')
    .trim()
    .toLowerCase();
}

/**
 * Build topic groups from a list of emails belonging to the same domain.
 *
 * 1. Group by subcategory (primary — LLM-assigned topic)
 * 2. Emails without subcategory fall back to threadId → normalised subject
 * 3. Sort groups by highest urgency desc, then most recent email
 */
function buildTopicGroups(emails) {
  // Step 1 — cluster by subcategory (primary grouping key)
  const subcatMap = {};
  const noSubcat = [];

  emails.forEach((e) => {
    if (e.subcategory) {
      if (!subcatMap[e.subcategory]) subcatMap[e.subcategory] = [];
      subcatMap[e.subcategory].push(e);
    } else {
      noSubcat.push(e);
    }
  });

  // Step 2 — fallback: group unclassified emails by threadId, then subject
  const threadMap = {};
  noSubcat.forEach((e) => {
    const key = e.threadId || e.id;
    if (!threadMap[key]) threadMap[key] = [];
    threadMap[key].push(e);
  });

  const subjectMap = {};
  const multiThreadGroups = [];
  Object.values(threadMap).forEach((group) => {
    if (group.length > 1) {
      multiThreadGroups.push(group);
      return;
    }
    const norm = normaliseSubject(group[0].subject);
    if (!subjectMap[norm]) subjectMap[norm] = [];
    subjectMap[norm].push(group[0]);
  });

  const allGroups = [
    ...Object.values(subcatMap),
    ...multiThreadGroups,
    ...Object.values(subjectMap),
  ];

  // Step 3 — compute group metadata & sort
  const enriched = allGroups.map((groupEmails) => {
    const sorted = [...groupEmails].sort(
      (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt),
    );
    const highestUrgency = Math.max(...sorted.map((e) => e.urgency));
    const unreadCount = sorted.filter((e) => !e.isRead).length;
    const latest = sorted[0];

    return {
      subcategory: latest.subcategory || null,
      label: latest.subject.replace(/^(RE|FWD|Fwd|Fw|Re):\s*/g, ''),
      emails: sorted,
      totalCount: sorted.length,
      unreadCount,
      highestUrgency,
      latestReceivedAt: latest.receivedAt,
      threadSummary: latest.threadSummary,
    };
  });

  // Sort: highest urgency first, then most recent
  enriched.sort((a, b) => {
    if (b.highestUrgency !== a.highestUrgency) return b.highestUrgency - a.highestUrgency;
    return new Date(b.latestReceivedAt) - new Date(a.latestReceivedAt);
  });

  return enriched;
}

/**
 * @param {Array} folderEmails — already filtered by activeFolder
 * @returns {Array} domainBundles sorted by urgency/recency
 */
export function useDomainBundles(folderEmails) {
  return useMemo(() => {
    // Group by primaryDomain + secondaryDomains (multi-domain emails appear in multiple buckets)
    const domainMap = {};
    folderEmails.forEach((e) => {
      const allDomains = [e.primaryDomain, ...(e.secondaryDomains || [])];
      const seen = new Set();
      allDomains.forEach((d) => {
        if (seen.has(d)) return;
        seen.add(d);
        if (!domainMap[d]) domainMap[d] = [];
        domainMap[d].push(e);
      });
    });

    const bundles = Object.entries(domainMap).map(([domainKey, emails]) => {
      const totalCount = emails.length;
      const unreadCount = emails.filter((e) => !e.isRead).length;
      const highestUrgency = Math.max(...emails.map((e) => e.urgency));
      const sorted = [...emails].sort(
        (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt),
      );
      const latestReceivedAt = sorted[0].receivedAt;
      const topicGroups = buildTopicGroups(emails);

      return {
        domainKey,
        domainLabel: DOMAIN_LABELS[domainKey] || domainKey,
        totalCount,
        unreadCount,
        highestUrgency,
        latestReceivedAt,
        summary: DOMAIN_SUMMARIES[domainKey] || '',
        topicGroups,
      };
    });

    // Sort bundles: highest urgency first, then by unread count, then recency
    bundles.sort((a, b) => {
      if (b.highestUrgency !== a.highestUrgency) return b.highestUrgency - a.highestUrgency;
      if (b.unreadCount !== a.unreadCount) return b.unreadCount - a.unreadCount;
      return new Date(b.latestReceivedAt) - new Date(a.latestReceivedAt);
    });

    return bundles;
  }, [folderEmails]);
}
